import React from 'react';

interface SkeletonProps {
  width?: string | number;
  height?: string | number;
  borderRadius?: string | number;
  style?: React.CSSProperties;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  width = '100%',
  height = 16,
  borderRadius = 6,
  style,
}) => (
  <div style={{
    width,
    height,
    borderRadius,
    background: 'linear-gradient(90deg, var(--border) 25%, rgba(255,255,255,0.3) 50%, var(--border) 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s ease-in-out infinite',
    ...style,
  }} />
);

export const SkeletonCard: React.FC = () => (
  <div style={{
    background: 'var(--bg-card)',
    borderRadius: 12,
    padding: 20,
    border: '1px solid var(--border)',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  }}>
    <Skeleton width={100} height={14} />
    <Skeleton width={60} height={28} />
    <Skeleton width={140} height={12} />
  </div>
);

export const SkeletonListItem: React.FC = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 0',
    borderBottom: '1px solid var(--border)',
  }}>
    <Skeleton width={40} height={40} borderRadius="50%" />
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Skeleton width="60%" height={14} />
      <Skeleton width="40%" height={12} />
    </div>
    <Skeleton width={80} height={14} />
  </div>
);

export const SkeletonTable: React.FC<{ rows?: number }> = ({ rows = 6 }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
    <div style={{
      display: 'grid',
      gridTemplateColumns: '2fr 1.5fr 1fr 1fr',
      gap: 16,
      padding: '12px 16px',
      borderBottom: '2px solid var(--border)',
    }}>
      {[...Array(4)].map((_, i) => (
        <Skeleton key={i} height={14} width="70%" />
      ))}
    </div>
    {[...Array(rows)].map((_, row) => (
      <div key={row} style={{
        display: 'grid',
        gridTemplateColumns: '2fr 1.5fr 1fr 1fr',
        gap: 16,
        padding: '14px 16px',
        borderBottom: '1px solid var(--border)',
      }}>
        {[...Array(4)].map((_, col) => (
          <Skeleton key={col} height={14} width={col === 0 ? '80%' : '60%'} />
        ))}
      </div>
    ))}
  </div>
);

export const SkeletonDashboard: React.FC = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
    {/* KPI Cards */}
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
      {[...Array(4)].map((_, i) => (
        <div key={i} style={{
          background: 'var(--bg-card)',
          borderRadius: 10,
          padding: 20,
          border: '1px solid var(--border)',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Skeleton width={40} height={40} borderRadius={10} />
            <Skeleton width={50} height={20} borderRadius={10} />
          </div>
          <Skeleton width={80} height={28} />
          <Skeleton width={120} height={12} />
        </div>
      ))}
    </div>
    {/* Quick Actions */}
    <div style={{ display: 'flex', gap: 10 }}>
      {[...Array(4)].map((_, i) => <Skeleton key={i} width={130} height={36} borderRadius={20} />)}
    </div>
    {/* Chart + Sidebar */}
    <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 24 }}>
      <div style={{
        background: 'var(--bg-card)',
        borderRadius: 10,
        padding: 20,
        border: '1px solid var(--border)',
        height: 340,
      }}>
        <Skeleton width="100%" height="100%" borderRadius={6} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div style={{
          background: 'var(--bg-card)',
          borderRadius: 10,
          padding: 20,
          border: '1px solid var(--border)',
        }}>
          {[...Array(3)].map((_, i) => <SkeletonListItem key={i} />)}
        </div>
        <div style={{
          background: 'var(--bg-card)',
          borderRadius: 10,
          padding: 16,
          border: '1px solid var(--border)',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 8,
        }}>
          {[...Array(6)].map((_, i) => <Skeleton key={i} height={40} borderRadius={6} />)}
        </div>
      </div>
    </div>
  </div>
);

export const SkeletonCalendar: React.FC = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 8px' }}>
      <Skeleton width={30} height={30} borderRadius={6} />
      <Skeleton width={180} height={22} />
      <Skeleton width={30} height={30} borderRadius={6} />
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
      {[...Array(7)].map((_, i) => (
        <Skeleton key={`h${i}`} height={20} style={{ margin: '0 auto' }} width={30} />
      ))}
      {[...Array(35)].map((_, i) => (
        <Skeleton key={`d${i}`} height={60} borderRadius={6} />
      ))}
    </div>
  </div>
);
